import React, { useState } from 'react';
import { string, bool, number, func } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureUser, userDisplayNameAsString } from '../../util/data';
import { countryCodes } from '../../translations/countryCodes';
import { Avatar, NamedLink } from '../../components';
import {
  extractYouTubeVideoId,
  isYouTubeUrl,
} from '../../components/FieldRichTextInput/nodes/YouTubeEmbedNode';
import {
  extractVimeoVideoId,
  isVimeoUrl,
} from '../../components/FieldRichTextInput/nodes/VimeoEmbedNode';

import css from './ProfileHeader.module.css';

// Convert country code to flag emoji (e.g., "US" -> flag)
const getCountryFlag = code => {
  if (!code || code.length !== 2) return null;
  const upper = code.toUpperCase();
  return String.fromCodePoint(...[...upper].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
};

// Get embed URL from a YouTube or Vimeo URL (autoplay for click-to-play)
const getEmbedUrl = (url, autoplay = false) => {
  if (!url) return null;
  if (isYouTubeUrl(url)) {
    const videoId = extractYouTubeVideoId(url);
    const params = autoplay ? '?rel=0&autoplay=1' : '?rel=0';
    return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}${params}` : null;
  }
  if (isVimeoUrl(url)) {
    const videoId = extractVimeoVideoId(url);
    const params = autoplay ? '?dnt=1&autoplay=1' : '?dnt=1';
    return videoId ? `https://player.vimeo.com/video/${videoId}${params}` : null;
  }
  return null;
};

// Get video thumbnail URL
const getVideoThumbnail = url => {
  if (!url) return null;
  if (isYouTubeUrl(url)) {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  }
  if (isVimeoUrl(url)) {
    // Vimeo thumbnails require an API call; we'll handle via oEmbed in an effect
    return null;
  }
  return null;
};

// Tabler icons (local SVG files)
const calendarCheckIcon = '/static/icons/calendar-check.svg';
const tagIcon = '/static/icons/tag.svg';

const ProfileHeader = props => {
  const {
    user,
    isCurrentUser,
    className,
    rootClassName,
    listingCount,
    onContactUser,
    viewport,
    vimeoThumbnailUrl,
  } = props;
  const intl = useIntl();
  const [showFullBio, setShowFullBio] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const ensuredUser = ensureUser(user);
  const displayName = userDisplayNameAsString(ensuredUser, '');
  const { bio, publicData } = ensuredUser?.attributes?.profile || {};

  // Get location from publicData - prefer displayLocation (user's privacy choice), fall back to city/state
  let location = null;
  const locationData = publicData?.location;

  let countryFlag = null;
  if (publicData?.displayLocation) {
    location = publicData.displayLocation;
    countryFlag = getCountryFlag(locationData?.country);
  } else if (locationData?.city) {
    // Format as "City, State" with flag emoji for country
    const parts = [locationData.city, locationData.state].filter(Boolean);
    location = parts.join(', ');
    countryFlag = getCountryFlag(locationData.country);
  }

  // Website URL (only display if bio >= 350 chars)
  const websiteUrl = publicData?.websiteUrl;
  const showWebsiteUrl = websiteUrl && bio && bio.length >= 350;
  // Display domain only (strip protocol and trailing slash)
  const websiteDisplayDomain = websiteUrl
    ? websiteUrl
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '')
    : null;
  // Ensure href has protocol
  const websiteHref =
    websiteUrl && !/^https?:\/\//i.test(websiteUrl) ? `https://${websiteUrl}` : websiteUrl;

  const classes = classNames(rootClassName || css.root, className);

  // Determine if bio needs "read more" toggle (more than ~150 characters)
  const isMobile = viewport && viewport.width > 0 && viewport.width < 550;
  const bioTruncateLength = isMobile ? 150 : 300;
  const bioNeedsTruncation = bio && bio.length > bioTruncateLength;
  const displayBio =
    bioNeedsTruncation && !showFullBio ? `${bio.substring(0, bioTruncateLength)}...` : bio;

  // Media: video or photo
  const shopVideoUrl = publicData?.shopVideoUrl;
  const shopPhotoUrl = publicData?.shopPhotoUrl;
  const embedUrl = getEmbedUrl(shopVideoUrl, true);
  const hasVideo = !!getEmbedUrl(shopVideoUrl);
  const hasPhoto = !hasVideo && !!shopPhotoUrl;
  const hasMedia = hasVideo || hasPhoto;
  // A Vimeo video's thumbnail comes with the profile (ProfilePage.duck loadData)
  const videoThumbnail = getVideoThumbnail(shopVideoUrl) || vimeoThumbnailUrl;

  // Stats content (reused in both layouts)
  const statsContent = (
    <>
      {ensuredUser.attributes.createdAt && (
        <div className={css.statItem}>
          <img src={calendarCheckIcon} alt="" className={css.statIcon} />
          <FormattedMessage
            id="ProfileHeader.memberSince"
            values={{
              date: intl.formatDate(ensuredUser.attributes.createdAt, {
                month: 'short',
                year: 'numeric',
              }),
            }}
          />
        </div>
      )}
      <div className={css.statItem}>
        <img src={tagIcon} alt="" className={css.statIcon} />
        <FormattedMessage id="ProfileHeader.listingCount" values={{ count: listingCount || 0 }} />
      </div>
    </>
  );

  // Media frame content (reused for inline mobile + standalone column)
  const mediaFrameContent = hasVideo ? (
    <div className={css.mediaFrame}>
      {videoPlaying ? (
        <iframe
          src={embedUrl}
          className={css.videoIframe}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          title={intl.formatMessage({ id: 'ProfileHeader.shopVideoTitle' })}
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <button
          type="button"
          className={css.videoThumbnailButton}
          onClick={() => setVideoPlaying(true)}
          aria-label={intl.formatMessage({ id: 'ProfileHeader.playVideo' })}
        >
          {videoThumbnail && (
            <img src={videoThumbnail} alt={displayName} className={css.shopPhoto} />
          )}
          <div className={css.playButton}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      )}
    </div>
  ) : hasPhoto ? (
    <div className={css.mediaFrame}>
      <img src={shopPhotoUrl} alt={displayName} className={css.shopPhoto} />
    </div>
  ) : null;

  // One row of actions for everyone: Message for visitors, Edit profile for the owner
  const actions =
    isCurrentUser || onContactUser ? (
      <div className={css.actions}>
        {isCurrentUser ? (
          <NamedLink className={css.actionButton} name="ProfileSettingsPage">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 20h4L19 9l-4-4L4 16v4ZM14 6l4 4" />
            </svg>
            <FormattedMessage id="ProfileHeader.editProfile" />
          </NamedLink>
        ) : (
          <button type="button" className={css.actionButton} onClick={onContactUser}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
            </svg>
            <FormattedMessage id="ProfileHeader.messageSeller" />
          </button>
        )}
      </div>
    ) : null;

  return (
    <div className={classes}>
      <div className={css.avatarWrapper}>
        <Avatar
          className={css.avatar}
          initialsClassName={css.avatarInitials}
          user={user}
          disableProfileLink
          placeholder="person"
        />
      </div>

      <div className={css.nameBlock}>
        <h1 className={css.displayName}>{displayName}</h1>
        {location && (
          <p className={css.location}>
            {countryFlag && <span aria-hidden="true">{countryFlag} </span>}
            {location}
          </p>
        )}
      </div>

      <div className={css.detailsBlock}>
        <div className={css.stats}>{statsContent}</div>

        {bio && (
          <div className={css.bioWrapper}>
            <p className={css.bio}>
              {displayBio}
              {bioNeedsTruncation && (
                <>
                  {' '}
                  <button
                    type="button"
                    className={css.readMoreButton}
                    onClick={() => setShowFullBio(!showFullBio)}
                  >
                    {showFullBio ? (
                      <FormattedMessage id="ProfileHeader.readLess" />
                    ) : (
                      <FormattedMessage id="ProfileHeader.readMore" />
                    )}
                  </button>
                </>
              )}
            </p>

            {/* Website URL - shown only when bio is expanded */}
            {showFullBio && showWebsiteUrl && (
              <a
                href={websiteHref}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={css.websiteUrl}
              >
                <img src="/static/icons/world.svg" alt="" className={css.websiteIcon} />
                {websiteDisplayDomain}
              </a>
            )}

            {/* Media inline after bio - below 1024px, shown when expanded */}
            {showFullBio && mediaFrameContent && (
              <div className={css.mediaInline}>{mediaFrameContent}</div>
            )}
          </div>
        )}

        {actions}
      </div>

      {/* Right column: the shop's video or photo (1024+) */}
      {hasMedia ? <div className={css.mediaColumn}>{mediaFrameContent}</div> : null}
    </div>
  );
};

ProfileHeader.defaultProps = {
  className: null,
  rootClassName: null,
  user: null,
  isCurrentUser: false,
  listingCount: 0,
  onContactUser: null,
};

ProfileHeader.propTypes = {
  className: string,
  rootClassName: string,
  user: propTypes.user,
  isCurrentUser: bool,
  listingCount: number,
  onContactUser: func,
  // A Vimeo video's thumbnail (ProfilePage.duck)
  vimeoThumbnailUrl: string,
};

export default ProfileHeader;
